import React, { useCallback, useMemo, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';

import { AgencyMsaRateAdminService, CsvParserService } from 'us-web-services';
import { AgencyMsaRate } from 'us-web-services/dist/types/AgencyMsaRate';
import { Table, TableInstance, CreateModal } from 'src/components/common/Table';
import DisplayService from '../../../util/DisplayService';
import PageStyles from '../../../styles/PageStyles';
import useAuthentication from '../../../store/useAuthentication';

function AgencyMsaRates() {
  const [authenticationState] = useAuthentication();
  const authenticatedUser = authenticationState.authentication.user;
  const isAgencyAdmin = authenticatedUser.roles.filter(role => role.code === 'AGENCY_ADMIN').length > 0;

  const [file, setFile] = useState(null);
  const tableRef = useRef<TableInstance>();
  const [parsed, setParsed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccessful, setUploadSuccessful] = useState(null);
  const [validCount, setValidCount] = useState(null);
  const [duplicateCount, setDuplicateCount] = useState(null);
  const [invalidMsaCount, setInvalidMsaCount] = useState(null);
  const [invalidRateCount, setInvalidRateCount] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const fileInput = useRef<HTMLInputElement>();
  const { classes } = PageStyles();

  const openConfirm = () => {
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
  };

  const showError = useCallback(error => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      'There was an unknown error updating the agency MSA rates.',
      false,
    );

    displayedError.autoDismiss = false;
    toast.error(displayedError.message);
    setUploadSuccessful(false);
  }, []);

  const resetAfterUpload = (response) => {
    const responseData = response.data.data;

    setFile(null);
    setParsed(false);
    setValidCount(null);
    setDuplicateCount(null);
    setInvalidMsaCount(null);
    setInvalidRateCount(null);
    setUploadSuccessful(responseData.success);

    if (responseData.errors && responseData.errors.length > 0) {
      const formattedErrors = responseData.errors.map(err => ({
        row: err.row,
        error: err.message,
      }));

      setValidationErrors(formattedErrors);
    } else {
      setValidationErrors([]);
    }
  };

  const getNumberOfDuplicatesInCSV = data => {
    const msaIds = data.map(row => row[0]);
    const uniqueMsaIds = new Set(msaIds);

    return msaIds.length - uniqueMsaIds.size;
  };

  const validateRate = (rate) => {
    if (Number.isNaN(rate) || !Number.isInteger(Number(rate))) {
      return { valid: false, reason: 'Rate must be a whole number' };
    }

    const rateNum = Number(rate);

    if (rateNum < 8) {
      return { valid: false, reason: 'Rate must be ≥ 8' };
    }

    if (rateNum > 100) {
      return { valid: false, reason: 'Rate must be ≤ 100' };
    }

    return { valid: true };
  };

  const processData = async (data) => {
    const startIndex = data[0][0]?.toLowerCase() === 'msa_id' ? 1 : 0;
    const dataRows = data.slice(startIndex);

    let valid = 0;
    const invalid = { msaCount: 0, rateCount: 0 };
    const errors = [];

    const duplicates = getNumberOfDuplicatesInCSV(dataRows);

    if (duplicates > 0) {
      errors.push({ row: 'N/A', error: `Found ${duplicates} duplicate MSA IDs in the CSV` });
    }

    dataRows.forEach((row, index) => {
      const rowNum = startIndex + index + 1;

      if (!row || !row[0]) {
        invalid.msaCount += 1;
        errors.push({
          row: rowNum,
          error: 'Empty or invalid row detected. Each row must contain a valid MSA ID.',
        });

        return;
      }

      const msaIdStr = row[0]?.toString();

      if (!msaIdStr || Number.isNaN(Number(msaIdStr)) || !Number.isInteger(Number(msaIdStr)) || Number(msaIdStr) <= 0) {
        invalid.msaCount += 1;
        errors.push({ row: rowNum, error: `Invalid MSA ID: ${msaIdStr}. Must be a positive integer.` });

        return;
      }

      for (let i = 1; i <= 4; i += 1) {
        const rate = row[i];
        const validation = validateRate(rate);

        if (!validation.valid) {
          invalid.rateCount += 1;
          errors.push({
            row: rowNum,
            error: `Invalid rate for care_for_${i}: ${rate} - ${validation.reason}`,
          });

          return;
        }
      }

      valid += 1;
    });

    setValidCount(valid);
    setDuplicateCount(duplicates);
    setInvalidMsaCount(invalid.msaCount);
    setInvalidRateCount(invalid.rateCount);
    setValidationErrors(errors);
    setParsed(true);
  };

  const processFile = async () => {
    setParsed(false);
    setValidCount(null);
    setDuplicateCount(null);
    setInvalidMsaCount(null);
    setInvalidRateCount(null);
    setValidationErrors([]);
    setUploadSuccessful(null);

    if (fileInput.current.files[0]) {
      setUploadSuccessful(null);
      setFile(fileInput.current.files[0]);
      const reader = new window.FileReader();

      reader.onload = async (e) => {
        const data = CsvParserService.parse(e.target.result.toString(), ',', false);

        await processData(data);
      };
      reader.readAsText(fileInput.current.files[0]);
    } else {
      toast.error('Uploaded file does not exist');
    }
  };

  const refreshAgencyMsaRates = () => tableRef.current?.fetchData();

  const uploadFile = async () => {
    if (file) {
      const formData = new FormData();

      formData.append('file', file);

      setShowConfirm(false);
      setUploading(true);

      await AgencyMsaRateAdminService
        .upload(formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(resetAfterUpload)
        .catch(showError)
        .finally(() => {
          setUploading(false);
          refreshAgencyMsaRates();
        });
    }
  };

  const addMsaRate = async (values) => {
    const data: AgencyMsaRate = {
      msaId: values.msaId,
      careFor1: values.careFor1,
      careFor2: values.careFor2,
      careFor3: values.careFor3,
      careFor4: values.careFor4,
    };

    await AgencyMsaRateAdminService.create(data);
    refreshAgencyMsaRates();
  };

  const deleteMsaRate = async (msaRate) => {
    await AgencyMsaRateAdminService.delete(msaRate.id);
    refreshAgencyMsaRates();
  };

  const downloadTemplate = () => {
    const csvContent = 'MSA_ID,care_for_1,care_for_2,care_for_3,care_for_4\n'
      + '1,48,48,51,53\n'
      + '2,30,40,50,60\n'
      + '3,20,22,24,26';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', 'agency_msa_rates_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: 'agencyRates',
        header: 'Agency MSA Rates',
        columns: [
          {
            header: 'MSA ID',
            accessorKey: 'msaId',
          },
          {
            header: 'Care For 1',
            accessorKey: 'careFor1',
          },
          {
            header: 'Care For 2',
            accessorKey: 'careFor2',
          },
          {
            header: 'Care For 3',
            accessorKey: 'careFor3',
          },
          {
            header: 'Care For 4',
            accessorKey: 'careFor4',
          },
        ],
      },
    ],
    [],
  );

  if (!isAgencyAdmin) {
    return (
      <Paper className={classes.paper}>
        <Typography variant='h6' color='error'>
          Access Denied
        </Typography>
        <Typography>
          You need the agency_admin role to access this tool.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Dialog open={showConfirm} onClose={closeConfirm}>
        <DialogTitle>Ready to upload?</DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to update the agency MSA rates with these {validCount} entries?
          </p>
          <p>
            This will replace existing rates for MSAs or add new entries for MSAs that don&apos;t have rates yet.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={uploadFile}
            disabled={
              uploading
              || validCount === 0
              || duplicateCount > 0
              || invalidMsaCount > 0
              || invalidRateCount > 0
            }
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant='h6' gutterBottom>
        Manage Agency MSA Rates
      </Typography>
      <p>
        Upload a .csv file to update the agency rates for Master Service Areas.
      </p>
      <p>
        <Button
          color='primary'
          onClick={downloadTemplate}
          variant='outlined'
          style={{ marginBottom: '1rem' }}
        >
          Download CSV Template
        </Button>
      </p>
      <Grid container spacing={2}>
        <Grid item lg={4}>
          <Button
            variant='contained'
            component='label'
            color='primary'
          >
            Select CSV file
            <input
              id='csv-file-agency-msa-rates'
              type='file'
              hidden
              ref={fileInput}
              onChange={() => {
                processFile();
              }}
              onClick={(e) => {
                e.currentTarget.value = '';
              }}
              accept='.csv'
            />
          </Button>
          {file &&
            <Typography variant='body2' style={{ marginTop: 8 }}>
              Selected file: {file.name}
            </Typography>
          }
        </Grid>
        <Grid item lg={4}>
          {parsed && (
            <Box>
              Valid entries: {validCount}
            </Box>
          )}
          {parsed && duplicateCount > 0 && (
            <Box
              style={{
                color: 'red',
                fontWeight: 'bold',
              }}
            >
              {duplicateCount}{' '}
              {duplicateCount === 1 ? 'duplicate MSA ID' : 'duplicate MSA IDs'} in CSV
            </Box>
          )}
          {parsed && invalidMsaCount > 0 && (
            <Box
              style={{
                color: 'red',
                fontWeight: 'bold',
              }}
            >
              {invalidMsaCount} invalid MSA {invalidMsaCount === 1 ? 'ID' : 'IDs'}
            </Box>
          )}
          {parsed && invalidRateCount > 0 && (
            <Box
              style={{
                color: 'red',
                fontWeight: 'bold',
              }}
            >
              {invalidRateCount} invalid {invalidRateCount === 1 ? 'rate' : 'rates'}
            </Box>
          )}
        </Grid>
        <Grid item lg={4}>
          {parsed && (
            <Button
              color='primary'
              disabled={
                uploading
                || validCount === 0
                || duplicateCount > 0
                || invalidMsaCount > 0
                || invalidRateCount > 0
              }
              onClick={openConfirm}
              variant='contained'
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
          {uploadSuccessful === true && (
            <div style={{ color: 'green' }}>
              <FontAwesomeIcon
                style={{
                  fontSize: 16,
                  marginRight: '0.75rem',
                }}
                icon={faCheckCircle}
              />
              <span>Upload successful!</span>
            </div>
          )}
          {uploadSuccessful === false && (
            <div style={{ color: 'red' }}>
              <FontAwesomeIcon
                style={{
                  fontSize: 16,
                  marginRight: '0.75rem',
                }}
                icon={faTimesCircle}
              />
              <span>Error occurred</span>
            </div>
          )}
        </Grid>
      </Grid>

      {validationErrors.length > 0 && (
        <Box mt={2} mb={2}>
          <Typography variant='h6' color='error'>
            Validation Errors
          </Typography>
          <Paper style={{ maxHeight: 200, overflow: 'auto', padding: '1rem' }}>
            {validationErrors.map((error) => (
              <Box key={error.row} mb={1}>
                <Typography color='error'>
                  {error.row !== 'N/A' ? `Row ${error.row}: ` : ''}{error.error}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      <Table
        columns={COLUMNS}
        ref={tableRef}
        retrieveData={AgencyMsaRateAdminService.findByFilter}
        addAction={() => setCreateModalOpen(true)}
        deleteAction={deleteMsaRate}
      />
      <CreateModal
        columns={COLUMNS}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={addMsaRate}
        title='Add Agency MSA Rate'
        buttonText='Add'
      />
    </Paper>
  );
}

export default withRouter(AgencyMsaRates);
